// Mobile "More" bottom sheet for the property-detail page.
//
// Mounted from the hero's top-right three-dot button (mobile only). It
// replaces the standalone action grid (AI post / Zalo / public link) and
// the inline "Lịch sử thay đổi" expander that used to live below the
// broker card. Rows route to existing dialogs:
//   - Lịch sử thay đổi      → ActivityHistoryDialog
//   - Tạo bài đăng AI       → GeneratePostDialog
//   - Chia sẻ Zalo          → caller-supplied handler (page-level helper)
//   - Chia sẻ liên kết      → caller-supplied handler (page-level helper)
//   - Xoá tin đăng          → DeletePropertyConfirmDialog
//
// The sheet itself is a `shadcn` `Sheet` with `side="bottom"` so it slides
// up over a dimmed page. We close the sheet before opening the next
// surface to keep the stacking clean.

import { useState } from "react";
import {
  ChevronRight,
  Clock,
  Share,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";

import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useActivities } from "@/features/activity/api";
import ActivityHistoryDialog from "@/features/activity/activity-history-dialog";
import { GeneratePostDialog } from "@/features/ai/components/generate-post-dialog";
import { DeletePropertyConfirmDialog } from "@/features/property/components/delete-property-confirm-dialog";
import { useCanManageProperty } from "@/features/property/hooks/use-can-manage-property";
import type { Property } from "@/types/property";

interface MobileMoreSheetProps {
  property: Property;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Triggered when the user picks "Chia sẻ Zalo". The parent owns the share
   *  helper so the desktop action grid and this sheet share one implementation. */
  onShareZalo: () => void;
  /** Triggered when the user picks "Chia sẻ liên kết công khai". Same rationale. */
  onSharePublicLink: () => void;
  /** Preview text shown under the public-link row. The parent decides the
   *  shape (origin + token or generic placeholder). */
  publicLinkPreview: string;
}

export function MobileMoreSheet({
  property,
  open,
  onOpenChange,
  onShareZalo,
  onSharePublicLink,
  publicLinkPreview,
}: MobileMoreSheetProps) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const canManage = useCanManageProperty(property);

  // page=0 / size=4 — same query the detail page already issues, so React
  // Query dedupes. We only consume `totalItems` to render the subtitle.
  const activitiesQuery = useActivities({
    entityType: "PROPERTY",
    entityId: property.id,
    page: 0,
    size: 4,
  });
  const totalActivities = activitiesQuery.data?.totalItems ?? 0;

  const buildingName = property.buildingName ?? "Bất động sản";
  const internalCode = property.internalCode ?? `#${property.id}`;
  const wardName = property.wardDisplayName;

  // Close the More sheet before opening the next surface so the new dialog
  // is the only thing on top. Without this, react-aria's focus trap stacks
  // weirdly and the dim background doubles up.
  const closeThen = (next: () => void) => {
    onOpenChange(false);
    // Defer the opener so the close animation has a chance to start; the
    // exact ms isn't important — even a single tick is enough.
    window.setTimeout(next, 0);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="gap-0 overflow-y-auto rounded-t-[20px] p-0 pb-6 sm:max-w-md sm:mx-auto"
          showCloseButton={false}
        >
          <SheetTitle className="sr-only">Tuỳ chọn bất động sản</SheetTitle>

          {/* Drag handle */}
          <div className="mx-auto mt-2.5 h-1 w-9 shrink-0 rounded-full bg-border-strong" />

          {/* Header */}
          <div className="flex items-start justify-between gap-3 px-4 pt-3 pb-3.5">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[16px] font-bold tracking-tight">
                {buildingName}
              </div>
              <div className="mt-0.5 truncate font-mono text-[12px] text-ink-faint">
                {internalCode} · {wardName}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Đóng"
              className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-muted"
            >
              <X aria-hidden="true" className="size-3.5" />
            </button>
          </div>

          {/* THÔNG TIN */}
          <SheetSectionHeader>THÔNG TIN</SheetSectionHeader>
          <SheetRow
            onClick={() => closeThen(() => setHistoryOpen(true))}
            iconChipClass="bg-muted text-ink-soft"
            icon={<Clock aria-hidden="true" className="size-[15px]" />}
            label="Lịch sử thay đổi"
            sublabel={`${totalActivities} hoạt động`}
          />

          {/* CHIA SẺ */}
          <SheetSectionHeader>CHIA SẺ</SheetSectionHeader>
          <SheetRow
            onClick={() => closeThen(() => setAiOpen(true))}
            iconChipClass="bg-gradient-to-br from-primary to-foreground text-primary-foreground"
            icon={<Sparkles aria-hidden="true" className="size-[15px]" />}
            label="Tạo bài đăng AI"
            sublabel="Viết tự động cho Facebook, Zalo"
          />
          <SheetRow
            onClick={() => closeThen(onShareZalo)}
            iconChip={
              <span
                aria-hidden
                className="flex size-7 items-center justify-center rounded-[8px] bg-zalo font-mono text-[12px] font-bold text-white"
              >
                Z
              </span>
            }
            label="Chia sẻ Zalo"
            sublabel="Tóm tắt + ảnh đầu tiên"
          />
          <SheetRow
            onClick={() => closeThen(onSharePublicLink)}
            iconChipClass="bg-muted text-ink-soft"
            icon={<Share aria-hidden="true" className="size-[14px]" />}
            label="Chia sẻ liên kết công khai"
            sublabel={publicLinkPreview}
            sublabelMono
          />

          {/* NGUY HIỂM — only the responsible agent / manager / owner */}
          {canManage ? (
            <>
              <SheetSectionHeader tone="danger">NGUY HIỂM</SheetSectionHeader>
              <SheetRow
                onClick={() => closeThen(() => setDeleteOpen(true))}
                iconChipClass="bg-rose-100 text-rose-600"
                icon={<Trash2 aria-hidden="true" className="size-[15px]" />}
                label="Xoá tin đăng"
                labelClass="text-destructive"
                sublabel="Cần xác nhận · không thể hoàn tác"
              />
            </>
          ) : null}

          <div className="mt-2 px-4 text-center text-[11px] text-muted-foreground">
            Vuốt xuống hoặc nhấn ✕ để đóng
          </div>
        </SheetContent>
      </Sheet>

      {/* Routed dialogs. Rendered as siblings so they can stay mounted even
          when the More sheet is closed (so e.g. dismissing the AlertDialog
          doesn't dismount its animation). */}
      <ActivityHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        entityType="PROPERTY"
        entityId={property.id}
        headerLabel={property.internalCode ?? `BĐS #${property.id}`}
        headerTitle={property.buildingName ?? "Bất động sản"}
      />
      <GeneratePostDialog
        property={property}
        open={aiOpen}
        onOpenChange={setAiOpen}
      />
      <DeletePropertyConfirmDialog
        property={property}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Internal: section header + row primitives
// ---------------------------------------------------------------------------

function SheetSectionHeader({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "danger";
}) {
  return (
    <div
      className={cn(
        "px-4 py-1.5 font-mono text-[11px] font-semibold tracking-[0.06em]",
        tone === "danger" ? "text-destructive" : "text-ink-faint",
      )}
    >
      {children}
    </div>
  );
}

interface SheetRowProps {
  onClick: () => void;
  /** Either a Lucide-style icon rendered into a default chip (`iconChipClass`
   *  controls the chip's bg/text), or a fully-custom `iconChip` element when
   *  the visual is non-uniform (e.g. the blue Zalo square). */
  icon?: React.ReactNode;
  iconChipClass?: string;
  iconChip?: React.ReactNode;
  label: string;
  labelClass?: string;
  sublabel: string;
  sublabelMono?: boolean;
}

function SheetRow({
  onClick,
  icon,
  iconChipClass,
  iconChip,
  label,
  labelClass,
  sublabel,
  sublabelMono = false,
}: SheetRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted"
    >
      {iconChip ?? (
        <span
          aria-hidden
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-[8px]",
            iconChipClass,
          )}
        >
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className={cn("text-[14px] font-semibold", labelClass)}>
          {label}
        </div>
        <div
          className={cn(
            "mt-0.5 truncate text-[12px] text-muted-foreground",
            sublabelMono ? "font-mono text-[11px]" : "",
          )}
        >
          {sublabel}
        </div>
      </div>
      <ChevronRight aria-hidden="true" className="size-3.5 shrink-0 text-ink-faint" />
    </button>
  );
}
